(ns app.front.view.admin.tenant-management.users-tab
  "Users and Service accounts tabs of the tenant detail view: one collapsible
  section per group of the tenant, the domain group first. Rows expand into
  the shared user editor (`app.front.view.user-management.components`) for
  that group's user-groups, so an edit never reaches another group."
  (:require [app.domain.util :as du]
            [app.front.ui :as ui]
            [app.front.ui.section :as section]
            [app.front.util :refer [for-frag]]
            [app.front.view.admin.tenant-management.user-subs :as user-subs]
            [app.front.view.user-management.components :as um]
            [app.front.view.user-management.model :as model]
            [app.front.view.user-management.state :as state]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]))

(defn- section-ids [section]
  (-> section :groups du/entity-id-set))

(defn- groups-editor
  "The section group's user-groups, with the membership rules applied."
  [tr id-prefix section]
  (fn [selected on-toggle]
    [um/group-checkboxes {:tr          tr
                          :id-prefix   id-prefix
                          :module-name (:parent-group-name section)
                          :groups      (:groups section)
                          :selected    selected
                          :on-toggle   on-toggle}]))

(defn- section-group-names
  "The user's user-groups *in this section's group*; its access elsewhere shows
  in that group's own section."
  [section user]
  (->> (:groups section)
       (filter (comp (set (:user/group-ids user)) :db/id))
       (map :group/name)))

(defn- user-editor [tr tenant-id section user]
  (let [parent-group-id (:parent-group-id section)
        service?        (model/service-account? user)]
    [um/user-editor
     {:tr            tr
      :scope         (user-subs/section-scope tenant-id parent-group-id)
      :user          user
      :id-prefix     (str "tm-" parent-group-id)
      :editable-ids  (section-ids section)
      :toggle-fn     (partial model/toggle-group {:groups (:groups section)})
      :groups-editor (groups-editor tr (str "tm-user-" parent-group-id "-" (:db/id user)) section)
      :names?        (not service?)
      :save-event    [::user-subs/save-user tenant-id parent-group-id]
      :extra         (when service?
                       [:div.mt-4
                        [ui/button {:kind     :secondary
                                    :size     :small
                                    :icon     i/plus
                                    :on-click #(>evt [::user-subs/create-api-key tenant-id (:db/id user)])}
                         (tr [:admin/tenant-create-api-key])]])
      :remove-button [ui/modal-confirmation-button
                      {:tr                  tr
                       :modal-title         (tr [:admin/tenant-remove-from-group])
                       :trigger-button-text (tr [:admin/tenant-remove-from-group])
                       :trigger-button-opts {:size :small :kind :danger :icon i/remove}
                       :on-yes-click        #(>evt [::user-subs/remove-user tenant-id (:db/id user) parent-group-id])}
                      (tr [:admin/tenant-remove-from-group-confirmation]
                          [(model/display-name user)
                           (:parent-group-name section)])]}]))

;; ---------------------------------------------------------------------------
;; One section per group
;; ---------------------------------------------------------------------------

(defn- section-heading
  "The group's name plus how many of its users match, mirroring the Features tab."
  [title user-count toggle-fn _collapsed?]
  [:h2.flex.items-center.gap-3.cursor-pointer.normal-case
   {:on-click toggle-fn}
   [:span title]
   [:span.text-sm.font-normal.text-gray-600 (str user-count)]])

(defn- group-section
  [{:keys [tr tenant-id section users kind add-label empty-label table]}]
  (let [parent-group-id (:parent-group-id section)
        scope           (user-subs/scope tenant-id)
        person?         (= :person kind)]
    [section/section
     (str "tenant-group-users-" parent-group-id)
     {:tr                   tr
      :class                "compact !my-0"
      :initially-collapsed? true
      :heading-class        "normal-case"
      :heading-component    (partial section-heading (:parent-group-name section) (count users))}
     nil
     [:div.flex.justify-end.mb-4
      [um/add-button {:scope scope :kind kind :target parent-group-id :label add-label}]]
     (if (seq users)
       [table {:tr            tr
               :id            (str "admin-tenant-" (name kind) "-table-" parent-group-id)
               :users         users
               :group-names   (partial section-group-names section)
               :expanded-func (partial user-editor tr tenant-id section)}]
       [:p.text-gray-500 empty-label])
     [um/add-modal {:tr            tr
                    :scope         scope
                    :kind          kind
                    :target        parent-group-id
                    :title         (tr [(if person?
                                          :admin/tenant-add-user-to-group
                                          :admin/tenant-add-service-account-to-group)]
                                       [(:parent-group-name section)])
                    :help          (when-not person? (tr [:admin/tenant-service-account-help]))
                    :groups-editor (groups-editor tr (str "tm-add-" (name kind)) section)
                    :submit-event  [(if person? ::user-subs/add-user ::user-subs/add-service-account) tenant-id]}]]))

;; ---------------------------------------------------------------------------
;; Tab entry points
;; ---------------------------------------------------------------------------

(defn- sections-list
  [{:keys [tr tenant-id sections users-sub kind] :as opts}]
  (let [query (<sub [::state/query (user-subs/scope tenant-id)])]
    (for-frag [section sections]
      (let [users (<sub [users-sub tenant-id (:parent-group-id section) query])]
        ;; While searching, only the groups with a match are worth showing.
        (when (or (str/blank? query) (seq users))
          ^{:key (:parent-group-id section)}
          [group-section (merge (select-keys opts [:add-label :empty-label :table])
                                {:tr        tr
                                 :tenant-id tenant-id
                                 :section   section
                                 :users     users
                                 :kind      kind})])))))

(defn users-tab [tr tenant-id]
  (let [sections (<sub [::user-subs/group-sections tenant-id])
        loading? (<sub [::user-subs/loading? tenant-id])]
    [:div.flex.flex-col.gap-2.mt-6
     [um/search-field {:tr    tr
                       :scope (user-subs/scope tenant-id)
                       :id    "tenant-users-search"
                       :label (tr [:admin/tenant-user-search-label])}]
     (cond
       (and loading? (empty? sections))
       (cc/inline-loading {:description (tr [:loading])})

       (empty? sections)
       [:p.text-gray-500 (tr [:admin/tenant-no-users])]

       :else
       [sections-list {:tr          tr
                       :tenant-id   tenant-id
                       :sections    sections
                       :users-sub   ::user-subs/section-persons
                       :kind        :person
                       :add-label   (tr [:admin/tenant-add-user])
                       :empty-label (tr [:admin/tenant-no-users-in-group])
                       :table       um/persons-table}])]))

(defn service-accounts-tab [tr tenant-id api-keys-enabled?]
  (let [sections (<sub [::user-subs/service-account-sections tenant-id])
        loading? (<sub [::user-subs/loading? tenant-id])]
    [:div.flex.flex-col.gap-2.mt-6
     (ui/help (tr [:admin/tenant-service-accounts-help]))
     (if-not api-keys-enabled?
       (ui/warning (tr [:admin/tenant-api-keys-disabled]))
       [:<>
        [um/search-field {:tr    tr
                          :scope (user-subs/scope tenant-id)
                          :id    "tenant-service-accounts-search"
                          :label (tr [:admin/tenant-service-account-search-label])}]
        (cond
          (and loading? (empty? sections))
          (cc/inline-loading {:description (tr [:loading])})

          (empty? sections)
          [:p.text-gray-500 (tr [:admin/tenant-no-service-accounts])]

          :else
          [sections-list {:tr          tr
                          :tenant-id   tenant-id
                          :sections    sections
                          :users-sub   ::user-subs/section-service-accounts
                          :kind        :service-account
                          :add-label   (tr [:admin/tenant-add-service-account])
                          :empty-label (tr [:admin/tenant-no-service-accounts-in-group])
                          :table       um/service-accounts-table}])
        [um/new-api-key-modal {:tr tr :scope (user-subs/scope tenant-id)}]])]))
